// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import { xLoadingOverlay } from "@ces/App/Handlers/XNG/Common";
import TextField, { TextFieldProps } from "@ces/MobiusOverrides/TextField/TextField";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getLocation, removeAbsoluteUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const parsedQuery = parseQueryString<{ externalError?: string }>(getLocation(state).search);
    const { allowCreateAccount } = getSettingsCollection(state).accountSettings;

    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        externalError: parsedQuery.externalError,
        createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        allowCreateAccount,
    };
};
const mapDispatchToProps = {
    signIn,
    // XNG-Change
    xLoadingOverlay,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MiniSignInStylesStyles {
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    titleGridItem?: GridItemProps;
    title?: TypographyProps;
    signInActionGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
    registerGridItem?: GridItemProps;
    registerTitle?: TypographyProps;
    registerButtonGridItem?: GridItemProps;
    registrationButton?: ButtonPresentationProps;
    separator?: InjectableCss;
    forgetPasswordLinkWrapper?: InjectableCss;
    passwordLabel?: TypographyProps;
    registerSiteMessage?: TypographyProps;
}

export const miniSignInStyles: MiniSignInStylesStyles = {};

const styles = miniSignInStyles;
const StyledForm = getStyledWrapper("form");

const MiniSignIn = ({
    signIn,
    accountSettings,
    createAccountUrl,
    externalError,
    xLoadingOverlay,
    allowCreateAccount,
}: Props) => {
    const [rememberMe, setRememberMe] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");
    const [showPassword, setShowPassword] = useState(false);

    const showRememberMe = accountSettings.rememberMe;

    const history = useHistory();

    const closePopup = () => {
        setTimeout(() => {
            hideAll();
        }, 300);
    };

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        const queryParams = parseQueryString<{ returnUrl?: string }>(window.location.search);
        const returnUrl = removeAbsoluteUrl(queryParams.returnUrl?.toString());

        setErrorMessage("");

        xLoadingOverlay({ key: "add", value: "sign-in" });
        signIn({
            userName,
            password: pass,
            rememberMe,
            returnUrl,
            onComplete: () => {
                xLoadingOverlay({ key: "clear", value: "sign-in" });
            },
            onError: (error, statusCode) => {
                xLoadingOverlay({ key: "clear", value: "sign-in" });
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const handleCreateNewAccount = (e: any) => {
        e.preventDefault();
        if (!createAccountUrl) {
            return;
        }

        closePopup();
        history.push(createAccountUrl);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    return (
        <>
            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                <GridContainer {...styles.signInGridContainer} gap={16}>
                    <GridItem {...styles.titleGridItem}>
                        <Typography {...styles.title}>{translate("Log In")}</Typography>
                    </GridItem>
                    <GridItem {...styles.userNameGridItem}>
                        <TextField
                            id="userName"
                            {...styles.userNameTextField}
                            placeholder={
                                accountSettings.useEmailAsUserName
                                    ? translate("Your Email")
                                    : translate("Your Username")
                            }
                            label={accountSettings.useEmailAsUserName ? translate("Email") : translate("Username")}
                            onChange={e => setUserName(e.currentTarget.value)}
                            value={userName}
                            error={userErrorMessage}
                            autoComplete="username"
                            data-test-selector="signIn_userName"
                            {...{ validationRules: "min:5|max:256" }}
                        />
                    </GridItem>
                    <GridItem {...styles.passwordGridItem}>
                        <TextField
                            id="password"
                            {...styles.passwordTextField}
                            label={
                                <StyledWrapper {...styles.forgetPasswordLinkWrapper}>
                                    <Typography {...styles.passwordLabel}>{translate("Password")}</Typography>
                                    <Link
                                        {...styles.forgotPasswordLink}
                                        type="button"
                                        onClick={() => setIsResetPasswordModalOpen(true)}
                                        data-test-selector="signIn_forgotPassword"
                                    >
                                        {translate("Forgot Password?")}
                                    </Link>
                                </StyledWrapper>
                            }
                            iconProps={{
                                ...styles.icon,
                                src: showPassword ? EyeOff : Eye,
                            }}
                            iconClickableProps={{
                                onClick: () => {
                                    setShowPassword(!showPassword);
                                },
                                type: "button",
                            }}
                            placeholder={translate("Your Password")}
                            onChange={e => setPassword(e.currentTarget.value)}
                            value={password}
                            error={passwordErrorMessage}
                            clickableText={password}
                            autoComplete="current-password"
                            data-test-selector="signIn_password"
                            type={showPassword ? "text" : "password"}
                            messageLabel={translate("Password")}
                        />
                    </GridItem>
                    <GridItem {...styles.signInActionGridItem}>
                        {showRememberMe && (
                            <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                <Checkbox
                                    uid="rememberMe"
                                    {...styles.rememberMeCheckbox}
                                    checked={rememberMe}
                                    onChange={(_, value) => setRememberMe(value)}
                                >
                                    {translate("Remember Me")}
                                </Checkbox>
                            </CheckboxGroup>
                        )}
                        <Button {...styles.signInButton} type="submit" data-test-selector="signIn_submit">
                            {translate("Log In")}
                        </Button>
                    </GridItem>
                    {allowCreateAccount && (
                        <>
                            <GridItem width={12}>
                                <StyledWrapper {...styles.separator} />
                            </GridItem>
                            <GridItem {...styles.registerGridItem}>
                                <Typography {...styles.registerTitle}>{translate("Request Account")}</Typography>
                                <Typography {...styles.registerSiteMessage}>
                                    {siteMessage("Custom_CreateAccountMessage")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.registerButtonGridItem}>
                                <Button
                                    variant="tertiary"
                                    {...styles.registrationButton}
                                    onClick={handleCreateNewAccount}
                                    data-test-selector="signIn_register"
                                    aria-label="Request Account"
                                >
                                    {translate("Request Account")}
                                </Button>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </StyledForm>

            <Modal
                headline={translate("Change Password")}
                {...styles.changePasswordModal}
                isOpen={isChangePasswordModalOpen}
                handleClose={onChangePasswordClose}
            >
                <SignInChangePasswordForm
                    enteredUserName={userName}
                    enteredPassword={password}
                    instructionsText={translate(
                        "A password change is required for your account before you may continue.",
                    )}
                    onPasswordChanged={onPasswordChanged}
                />
            </Modal>
            <Modal
                headline={translate("Reset Password")}
                {...styles.resetPasswordModal}
                isOpen={isResetPasswordModalOpen}
                handleClose={onResetPasswordClose}
                data-test-selector="resetPasswordModal"
            >
                <SignInResetPasswordForm onClose={onResetPasswordClose} />
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniSignIn);
